import Footer from "../Footer";
import Navbar from "../Navbar";
import SEO from "../SEO";

const AccountDeletionPolicy: React.FC = () => {
    const policySections = [
        {
            title: "1. Introduction",
            content: `You have the right to request deletion of your account with ${import.meta.env.VITE_COMPANY_NAME} at any time. This policy outlines the process and implications of account deletion.`,
        },
        {
            title: "2. How to Request Deletion",
            content: `Submit a request via ${import.meta.env.VITE_COMPANY_EMAIL} or through the settings section in the mobile application.`,
        },
        {
            title: "3. Data Retention",
            content: "Some data may be retained as required by law or for legitimate business purposes (e.g., active loans, fraud prevention, regulatory reporting). Unnecessary data will be permanently purged within 30 days of request.",
        },
        {
            title: "4. Implications of Deletion",
            list: [
                "Loss of access to account history and active services.",
                "Irreversible removal of personal preferences and profile settings.",
                "Active loans must be fully settled before account closure can be finalized.",
            ],
        },
    ];

    return (
        <div className="min-h-screen bg-white text-text-primary font-mono selection:bg-secondary selection:text-white ">
            <SEO 
                title="Account Deletion Policy"
                description={`Guidelines for account closure and data retention at ${import.meta.env.VITE_COMPANY_NAME}. Learn how to request deletion and what happen to your data.`}
                keywords={`account deletion, data privacy, ${import.meta.env.VITE_COMPANY_NAME} policies`}
            />
            <Navbar />

            {/* Official Registry Hero */}
            <section className="w-full bg-text-primary py-32 md:py-48 text-center px-6 relative overflow-hidden border-b-8 border-secondary mb-24">
                <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
                    style={{ backgroundImage: `linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

                <div className="relative z-10 max-w-5xl mx-auto">
                    <div className="inline-flex items-center gap-4 px-6 py-2 bg-white/5 border border-white/10 text-secondary text-[10px] font-black uppercase tracking-[0.5em] mb-12">
                        <div className="w-2 h-2 bg-secondary animate-pulse" />
                        Access_Closure_Protocol_v1.0
                    </div>
                    <h1 className="text-5xl md:text-[6rem] font-black tracking-tighter leading-[0.85] mb-12 uppercase text-white italic">
                        Account <span className="text-secondary not-italic">Deletion.</span>
                    </h1>
                    <p className="text-white/60 text-lg md:text-2xl max-w-3xl mx-auto font-bold leading-relaxed border-y border-white/10 py-10 uppercase tracking-tight">
                        Guidelines for account closure and data retention. <br />
                        <span className="text-white">Administered by {import.meta.env.VITE_COMPANY_NAME} Support.</span>
                    </p>
                </div>
            </section>

            <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 pb-40">
                <div className="border-t-4 border-text-primary">
                    {policySections.map((section, idx) => (
                        <div key={idx} className="border-b border-text-primary/10 py-16 group">
                            <div className="flex flex-col gap-10">
                                <div className="flex items-start gap-8">
                                    <span className="text-secondary font-black text-xs pt-1">CL_00{idx + 1}</span>
                                    <h2 className="text-3xl md:text-5xl font-black text-text-primary leading-tight uppercase tracking-tighter italic">
                                        {section.title}
                                    </h2>
                                </div>

                                <div className="pl-16 md:pl-24 space-y-8">
                                    {section.content && (
                                        <p className="text-text-secondary font-bold text-lg leading-relaxed uppercase tracking-tight border-l-4 border-text-primary/5 pl-8 italic">
                                            {section.content}
                                        </p>
                                    )}
                                    {section.list && (
                                        <ul className="space-y-6">
                                            {section.list.map((li, i) => (
                                                <li key={i} className="flex items-start gap-4 text-text-primary font-bold text-base leading-relaxed">
                                                    <div className="w-2 h-2 bg-secondary mt-2 shrink-0" />
                                                    <span className="uppercase tracking-tight">{li}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default AccountDeletionPolicy;
